import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { Text } from '@nula/core-ui';
import { SettingsShell, type SettingsTab } from '@/features/settings/components/settings-shell';
import { isDesktop } from '@/lib/platform';
import { useUpdateScheduler } from '@/lib/updater/use-update-scheduler';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { AccountTab } from '@/features/settings/components/tabs/account-tab';
import { AppearanceTab } from '@/features/settings/components/tabs/appearance-tab';
import { MeetingsTab } from '@/features/recordings/components/meetings-tab';
import { NotificationsTab } from '@/features/settings/components/tabs/notifications-tab';
import { SubscriptionTab } from '@/features/settings/components/tabs/subscription-tab';
import { UpdatesTab } from '@/features/settings/components/tabs/updates-tab';
import { GeneralTab } from '@/features/workspaces/components/settings/tabs/general-tab';
import { MembersTab } from '@/features/workspaces/components/settings/tabs/members-tab';
import { SubscriptionTab as WorkspaceSubscriptionTab } from '@/features/workspaces/components/settings/tabs/subscription-tab';
import pkg from '../../../package.json';
import styles from './settings-modal.module.css';

type UserTabId = 'account' | 'appearance' | 'notifications' | 'subscription' | 'meetings' | 'updates';
type WorkspaceTabId = 'ws-general' | 'ws-members' | 'ws-billing';
type TabId = UserTabId | WorkspaceTabId;

const USER_TABS: SettingsTab[] = [
  { id: 'account',       label: 'Account',       icon: 'person'        },
  { id: 'appearance',    label: 'Appearance',     icon: 'palette'       },
  { id: 'meetings',      label: 'Meetings',       icon: 'mic'           },
  { id: 'notifications', label: 'Notifications',  icon: 'notifications' },
  { id: 'subscription',  label: 'Subscription',   icon: 'credit_card'   },
];

const DESKTOP_TAB_ID = 'updates';

const WORKSPACE_ADMIN_TABS: SettingsTab[] = [
  { id: 'ws-general',  label: 'General',  icon: 'settings' },
  { id: 'ws-members',  label: 'Members',  icon: 'group'    },
];

// Billing is owner-only: every billing endpoint is owner-gated server-side, so
// an admin who saw this tab would only hit 403s.
const WORKSPACE_OWNER_TABS: SettingsTab[] = [
  { id: 'ws-billing',  label: 'Billing',  icon: 'credit_card' },
];

export interface SettingsModalProps {
  open: boolean;
  initialTabId?: string;
  onClose: () => void;
}

export function SettingsModal({ open, initialTabId = 'account', onClose }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>(initialTabId as TabId);
  const { user } = useAuthStore(useShallow(s => ({ user: s.user })));
  // Read-only: UpdateNotifier owns the schedule and the notification. Running
  // a second scheduler here would double-notify.
  const { availableVersion } = useUpdateScheduler({ enabled: false, isRecording: false });

  // The modal stays mounted while `open` toggles, so useState alone would ignore
  // a later initialTabId (e.g. the sidebar's "Upgrade" deep-linking to
  // Subscription). Re-drive the active tab whenever the modal opens to a tab.
  useEffect(() => {
    if (open) setActiveTab(initialTabId as TabId);
  }, [open, initialTabId]);

  const workspace = useWorkspaceStore(s =>
    s.activeWorkspaceId ? s.workspaces.find(w => w.id === s.activeWorkspaceId) ?? null : null,
  );

  const isWorkspaceAdmin =
    workspace !== null &&
    workspace.type === 'team' &&
    (workspace.role === 'owner' || workspace.role === 'admin');
  const isWorkspaceOwner =
    workspace !== null && workspace.type === 'team' && workspace.role === 'owner';

  // If the active tab belongs to the workspace section but access was lost, reset.
  // Billing (owner-only) resets for a non-owner; General/Members reset for a
  // non-admin.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const tab = activeTab as string;
    if (tab === 'ws-billing' && !isWorkspaceOwner) setActiveTab('account');
    else if (tab.startsWith('ws-') && !isWorkspaceAdmin) setActiveTab('account');
  }, [activeTab, isWorkspaceAdmin, isWorkspaceOwner]);

  const tabs: SettingsTab[] = [
    ...USER_TABS,
    // Web builds update on reload; there is nothing for the updater to do.
    ...(isDesktop
      ? [{
          id: DESKTOP_TAB_ID,
          label: 'Updates',
          icon: 'system_update',
          // Survives the notification being dismissed, so a waiting update
          // stays discoverable.
          badge: Boolean(availableVersion),
        } satisfies SettingsTab]
      : []),
    ...(isWorkspaceAdmin ? WORKSPACE_ADMIN_TABS : []),
    ...(isWorkspaceOwner ? WORKSPACE_OWNER_TABS : []),
  ];

  const tabContent: Record<TabId, React.ReactNode> = {
    account:       <AccountTab user={user} />,
    appearance:    <AppearanceTab />,
    meetings:      <MeetingsTab />,
    notifications: <NotificationsTab />,
    subscription:  <SubscriptionTab />,
    updates:       <UpdatesTab />,
    'ws-general':  workspace ? <GeneralTab workspace={workspace} /> : null,
    'ws-members':  workspace ? <MembersTab workspace={workspace} /> : null,
    'ws-billing':  <WorkspaceSubscriptionTab />,
  };

  return (
    <SettingsShell
      open={open}
      title="Settings"
      tabs={tabs}
      activeTabId={activeTab}
      onTabChange={(id) => setActiveTab(id as TabId)}
      onClose={onClose}
      workspaceSectionLabel={isWorkspaceAdmin ? 'Workspace' : undefined}
      footer={
        <Text size="xs" type="secondary" data-testid="settings-version" className={styles.versionTag}>
          🥞 Nula {pkg.version} ({__GIT_HASH__})
        </Text>
      }
    >
      {tabContent[activeTab]}
    </SettingsShell>
  );
}
